// src/components/Dashboard.js
import React, { useMemo } from "react";

function countBy(courses, field) {
  const counts = {};
  courses.forEach((c) => {
    const key = c[field] || "Unassigned";
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

function isRecentlyUpdated(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d)) return false;
  return (new Date() - d) / 86400000 <= 30;
}

function CountCard({ label, value, accentClass = "", onClick, hint = "View in catalogue →" }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`dash-card ${accentClass} ${clickable ? "dash-card--clickable" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
    >
      <span className="dash-card-label">{label}</span>
      <span className="dash-card-value">{value}</span>
      {clickable && <span className="dash-card-hint">{hint}</span>}
    </div>
  );
}

const PRACTICE_ACCENT = {
  "Data & AI":       "dash-card--teal",
  "Cloud & Digital": "dash-card--green",
  "TL&M":            "dash-card--purple",
};

const SOLUTION_ACCENT = {
  "Deep Skilling":   "dash-card--blue",
  "Transformative":  "dash-card--orange",
  "BootCamp":        "dash-card--pink",
  "Practice Overview":         "dash-card--overall",
};

export function Dashboard({ courses, loading, error, onPriorityClick, onNewClick, onUpdatedClick }) {
  const stats = useMemo(() => {
    const total = courses.length;
    const priorityCount = courses.filter((c) => c.priority).length;
    const newCount = courses.filter((c) => c.isNew).length;
    const updatedCount = courses.filter((c) => isRecentlyUpdated(c.modified)).length;
    const byPractice = countBy(courses, "practice");
    const bySolutionType = countBy(courses, "solutionType");
    return { total, priorityCount, newCount, updatedCount, byPractice, bySolutionType };
  }, [courses]);

  if (loading) return <div className="dash-loading"><span>Loading dashboard…</span></div>;
  if (error) return <div className="dash-error"><span>⚠ Couldn't load dashboard data: {error}</span></div>;
  if (stats.total === 0) return (
    <div className="dash-empty">
      <p>No programme data available yet.</p>
      <p className="dash-empty-sub">Check the SharePoint connection or try again shortly.</p>
    </div>
  );

  return (
    <div className="dashboard">
      <h3 className="dash-section-title">Overview</h3>
      <div className="dash-cards-grid">
        <CountCard label="Total Programs" value={stats.total} />
        <CountCard
          label="🔥 Sales Motion"
          value={stats.priorityCount}
          accentClass="dash-card--accent"
          onClick={onPriorityClick}
        />
        <CountCard
          label="✨ Newly Added"
          value={stats.newCount}
          accentClass="dash-card--new"
          onClick={stats.newCount > 0 ? onNewClick : undefined}
          hint="View in catalogue →"
        />
        <CountCard
          label="🔄 Recently Updated"
          value={stats.updatedCount}
          accentClass="dash-card--updated"
          onClick={stats.updatedCount > 0 ? onUpdatedClick : undefined}
          hint="Updated in last 30 days →"
        />
      </div>

      <h3 className="dash-section-title">Programs by Practice</h3>
      <div className="dash-cards-grid">
        {Object.entries(stats.byPractice).map(([practice, count]) => (
          <CountCard key={practice} label={practice} value={count} accentClass={PRACTICE_ACCENT[practice] || ""} />
        ))}
      </div>

      <h3 className="dash-section-title">Programs by Solution Type</h3>
      <div className="dash-cards-grid">
        {Object.entries(stats.bySolutionType).map(([type, count]) => (
          <CountCard key={type} label={type} value={count} accentClass={SOLUTION_ACCENT[type] || ""} />
        ))}
      </div>
    </div>
  );
}
